import { useEffect, useState } from "react";

import type { SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
} from "@tanstack/react-table";

import type { DataTableProps } from "@/shared/types";
import { Button } from "@/shared/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

import { cn } from "@/shared/lib/utils";
import { Skeleton } from "../skeleton";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileDataTable } from "./MobileDataTable";
import { Label } from "../label";
import { ActiveFilters } from "@/features/ActiveFilters";

export function DataTable<TData, TValue>({
  columns,
  data,
  pageCount,
  page,
  setPage,
  isLoading,
  selectedState,
  setSelectedState,
  setAllSelectedState,
  getRowId,
  selectMobileColumn,
  found,
  refetch,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const [rowSelection, setRowSelection] = useState({});

  const isMobile = useIsMobile();

  const handlePreviousPage = () => {
    if (setPage && page) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (setPage && page) {
      setPage(page + 1);
    }
  };

  const handleLastPage = () => {
    if (setPage) {
      setPage(pageCount || 0);
    }
  };

  const handleFirstPage = () => {
    if (setPage) {
      setPage(1);
    }
  };

  const table = useReactTable({
    data: data || [],
    pageCount,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    enableRowSelection: true,
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    state: { sorting, rowSelection },
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    debugTable: true,
  });

  useEffect(() => {
    if (setSelectedState) {
      const rows = table.getSelectedRowModel().rows;
      setSelectedState(rows.map((row) => row.original));
    }
  }, [rowSelection]);

  useEffect(() => {
    if (selectedState?.length === 0) {
      const rows = table.getSelectedRowModel().rows;

      if (rows.length > 0 && selectedState?.length === 0) {
        setRowSelection({});
      }
      table.getIsAllPageRowsSelected() && setAllSelectedState?.(true);
    }
  }, [selectedState]);

  if (isMobile)
    return (
      <MobileDataTable
        data={data}
        page={page}
        setPage={setPage}
        pageCount={pageCount}
        isLoading={isLoading}
        table={table}
        selectMobileColumn={selectMobileColumn}
        refetch={refetch}
      />
    );

  return (
    <div className="relative mt-5">
      {refetch && <ActiveFilters refetch={refetch} />}
      <div className="rounded-md border border-strokeColor">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className={cn("px-4")}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array.from({ length: 10 }, (_, index) => index)].map(
                (index) => (
                  <TableRow key={index}>
                    <TableCell className="pl-4 pr-0">
                      <Skeleton className="h-10 w-full rounded-r-none" />
                    </TableCell>
                    {table.getHeaderGroups()[0].headers.map((_, cellIndex) => (
                      <TableCell className="px-0" key={cellIndex}>
                        <Skeleton className="h-10 w-full rounded-none" />
                      </TableCell>
                    ))}
                    <TableCell className="pl-0 pr-4">
                      <Skeleton className="h-10 w-full rounded-l-none" />
                    </TableCell>
                  </TableRow>
                ),
              )
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="px-4 text-textColor" key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-textColor"
                >
                  Ничего нет.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between py-4">
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1 || isLoading || !page}
            onClick={handleFirstPage}
            className="text-textColor"
          >
            На первую страницу
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLastPage}
            className="text-textColor"
            disabled={page === pageCount || isLoading || pageCount === 0}
          >
            На последнюю страницу
          </Button>
        </div>
        {found && <Label>Найдено: {found}</Label>}
        <div className="flex gap-2">
          {/* <SelectLengthPageModal /> */}
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || isLoading || !setPage}
              onClick={handlePreviousPage}
              className="text-textColor"
            >
              Назад
            </Button>
            {pageCount ? (
              <div className="text-sm text-textColor">
                {page} из {pageCount}
              </div>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              className="text-textColor"
              disabled={page === pageCount || isLoading || pageCount === 0}
            >
              Вперед
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
